import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import LogoWhite from '../assets/LOGO_WHITE.png';
import Footer from '../components/Footer';

const BG_01 = "https://firebasestorage.googleapis.com/v0/b/outlne-6a95c.firebasestorage.app/o/ui_images_backgrounds%2FBG_01.jpg?alt=media&token=9e891581-27bf-4bdd-83cf-a39b44af9324";
const BG_02 = "https://firebasestorage.googleapis.com/v0/b/outlne-6a95c.firebasestorage.app/o/ui_images_backgrounds%2FBG_02.jpg?alt=media&token=b4394dc2-756e-41e8-9cfd-53c25137835d";
const BG_03 = "https://firebasestorage.googleapis.com/v0/b/outlne-6a95c.firebasestorage.app/o/ui_images_backgrounds%2FBG_03.jpg?alt=media&token=989909db-898b-4f93-babf-0262e968ddff";
const BG_04 = "https://firebasestorage.googleapis.com/v0/b/outlne-6a95c.firebasestorage.app/o/ui_images_backgrounds%2FBG_04.jpg?alt=media&token=b895a70e-d345-48b9-aa06-a359057f059a";
const BG_05 = "https://firebasestorage.googleapis.com/v0/b/outlne-6a95c.firebasestorage.app/o/ui_images_backgrounds%2FBG_05.jpg?alt=media&token=d91d94d3-064a-4efa-8bcf-f37cd4a04714";

export default function Careers() {
  useEffect(() => {
    document.title = "Careers at Outlne | Join Our Team";
  }, []);

  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const [activeRole, setActiveRole] = useState(null);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [activeGrid, setActiveGrid] = useState(0);
  const [scrollPosition, setScrollPosition] = useState({ x: 0, y: 0 });
  const [bgIndex, setBgIndex] = useState(0);
  const [bgPosition, setBgPosition] = useState({ x: 0, y: 0 });
  const [bgScale, setBgScale] = useState(1);
  const [bgOpacity, setBgOpacity] = useState(0);
  const [gridOpacity, setGridOpacity] = useState(1);
  const [pageLoaded, setPageLoaded] = useState(false);

  const lastGridChangeTime = useRef(0);
  const bgIntervalRef = useRef(null);
  const bgAnimationRef = useRef(null);

  const backgrounds = [BG_01, BG_02, BG_03, BG_04, BG_05];

  // Check screen size
  useEffect(() => {
    const checkScreenSize = () => {
      const width = window.innerWidth;
      const mobile = width < 768;
      const tablet = width >= 768 && width < 1224;
      
      setIsMobile(mobile);
      setIsTablet(tablet);
      setWindowWidth(width);
    };
    
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // Page load effect - fade out grid and fade in background
  useEffect(() => {
    const timer = setTimeout(() => {
      setPageLoaded(true);
      setGridOpacity(0);
      setBgOpacity(0);
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  // Optimized background slideshow
  useEffect(() => {
    const preloadImages = () => {
      backgrounds.forEach(src => {
        const img = new Image();
        img.src = src;
      });
    };
    preloadImages();

    const changeBackground = () => {
      setBgIndex(prev => (prev + 1) % backgrounds.length);
      
      const direction = Math.random() > 0.5 ? 1 : -1;
      const axis = Math.random() > 0.5 ? 'x' : 'y';
      
      setBgPosition({ x: 0, y: 0 });
      setBgScale(1.05);
      
      setTimeout(() => {
        setBgPosition({
          x: axis === 'x' ? direction * 5 : 0,
          y: axis === 'y' ? direction * 5 : 0
        });
      }, 10);
    };

    if (pageLoaded) {
      changeBackground();
      bgIntervalRef.current = setInterval(changeBackground, 8000);
    }

    return () => {
      clearInterval(bgIntervalRef.current);
      if (bgAnimationRef.current) {
        cancelAnimationFrame(bgAnimationRef.current);
      }
    };
  }, [pageLoaded]);

  // Grid animation - only run after page has loaded
  useEffect(() => {
    if (!pageLoaded) return;

    let animationFrame;
    let lastTime = 0;
    const scrollSpeed = 10;

    const animateScroll = (time) => {
      if (!lastTime) lastTime = time;
      const delta = (time - lastTime) / 1000;
      lastTime = time;
      
      const timeSinceChange = time - lastGridChangeTime.current;
      
      if (timeSinceChange > 100) {
        setScrollPosition(prev => ({
          x: (prev.x + scrollSpeed * delta) % 80,
          y: (prev.y + scrollSpeed * delta) % 80
        }));
      }
      
      animationFrame = requestAnimationFrame(animateScroll);
    };

    animationFrame = requestAnimationFrame(animateScroll);

    const interval = setInterval(() => {
      lastGridChangeTime.current = performance.now();
      setScrollPosition({ x: 0, y: 0 });
      setActiveGrid((prev) => (prev + 1) % 3);
    }, 4000);

    return () => {
      cancelAnimationFrame(animationFrame);
      clearInterval(interval);
    };
  }, [pageLoaded]);

  // Grid styles
  const gridStyles = [
    {
      transform: `translate(-${scrollPosition.x}px, -${scrollPosition.y}px)`,
      backgroundSize: '40px 40px',
      display: activeGrid === 0 ? 'block' : 'none',
      opacity: gridOpacity,
      transition: 'opacity 1s ease'
    },
    {
      transform: `translate(-${scrollPosition.x}px, -${scrollPosition.y}px) skewX(-15deg)`,
      backgroundSize: '50px 40px',
      backgroundPositionX: '20px',
      display: activeGrid === 1 ? 'block' : 'none',
      opacity: gridOpacity,
      transition: 'opacity 1s ease'
    },
    {
      transform: `translate(-${scrollPosition.x}px, -${scrollPosition.y}px) perspective(500px) rotateX(15deg)`,
      backgroundSize: '40px 30px',
      backgroundPositionY: '10px',
      display: activeGrid === 2 ? 'block' : 'none',
      opacity: gridOpacity,
      transition: 'opacity 1s ease'
    }
  ];

  const roles = [
    {
      id: 1,
      title: "Co-Founder (Product & Strategy)",
      shortDesc: "Shape the vision and lead our interior design editor platform with equity partnership.",
      fullTitle: "Co-Founder / Product & Strategy Lead",
      location: "Remote (Global)",
      compensation: "Equity-based with profit sharing",
      mission: "As a Co-Founder, you will work alongside the founder in building the future of interior design technology. You'll help shape the strategic vision, product roadmap, and company culture from the ground up. This is a true partnership where you'll have significant ownership and autonomy to build a revolutionary AI-powered interior design platform that transforms how modern interior designers work.",
      lookingFor: [
        "Thinks like a founder: Strategic, resourceful, and obsessed with solving real problems for interior designers",
        "Product visionary: Strong opinions on what makes exceptional design software with the ability to translate user needs into a compelling roadmap",
        "Multi-disciplinary leader: Comfortable with product design, user research, business strategy, and team building",
        "Design industry passion: Deep understanding of interior design workflows, pain points, and market opportunities",
        "Leadership mindset: Excited to establish company culture and eventually build and mentor teams"
      ]
    },
    {
      id: 2,
      title: "Full Stack Software Developer (AI Focus)",
      shortDesc: "Bridge creative vision with technical execution for our Interior design platform.",
      fullTitle: "Full Stack Software Developer (AI Focus) & Prompt Engineer",
      location: "Remote (Global)",
      compensation: "$120,000 - $160,000 + Equity",
      mission: "Bridge the gap between creative interior design vision and technical execution. You'll develop sophisticated prompt systems that guide our AI to generate precise interior design transformations, 3D model placements, and realistic room visualizations. Your work will directly impact how designers interact with AI to create stunning, production-ready interior designs.",
      lookingFor: [
        "Technical foundation: Strong knowledge of HTML, CSS, JavaScript and modern frameworks (React/JSX experience preferred)",
        "AI fluency: Expertise in structuring detailed, context-rich prompts for visual AI models with technical precision",
        "Creative problem-solving: Passion for experimenting with new techniques to achieve better AI-generated interior design results",
        "Design understanding: Ability to translate interior design concepts into descriptive language for efficient results"
      ]
    },
    {
      id: 3,
      title: "Sales Development Representative",
      shortDesc: "Drive growth by connecting with interior design professionals and studios.",
      fullTitle: "Sales Development Representative",
      location: "Remote (US Preferred)",
      compensation: "$65,000 - $85,000 + Commission (OTE $90,000 - $120,000)",
      mission: "Be the voice of Outlne to the interior design community. You'll identify and engage with design professionals, studios, and agencies to demonstrate how our AI-powered editor can transform their workflow. Your role is crucial in building relationships and creating opportunities with design professionals who need advanced visualization tools.",
      lookingFor: [
        "Excellent communicator: Strong verbal and written skills with ability to articulate complex technical benefits simply",
        "Design industry knowledge: Understanding of interior design business models, pain points, and decision-making processes",
        "Proactive outreach: Experience with cold outreach, email campaigns, and social selling to design professionals",
        "CRM proficiency: Experience with sales tools and pipeline management",
        "Goal-oriented: Driven by targets and metrics with proven track record of meeting/exceeding quotas"
      ]
    },
    {
      id: 4,
      title: "Social Media & Content Manager",
      shortDesc: "Build our brand presence and engage the interior design community.",
      fullTitle: "Social Media & Content Manager",
      location: "Remote (US Preferred)",
      compensation: "$70,000 - $90,000",
      mission: "Shape Outlne's voice and presence across social platforms. You'll create compelling content that showcases our AI interior design capabilities, engages the design community, and positions us as thought leaders in design technology. Your work will attract designers, studios, and agencies to our platform through authentic, valuable content.",
      lookingFor: [
        "Content creation expert: Proven ability to create engaging visual and written content for design/creative audiences",
        "Platform mastery: Deep understanding of Instagram, Pinterest, LinkedIn, and TikTok for B2B creative services",
        "Design aesthetic: Strong eye for visual storytelling and understanding of what resonates with design professionals",
        "Analytical mindset: Experience with social analytics, growth metrics, and content performance optimization",
        "Community building: Skills in engaging audiences, responding to feedback, and building authentic relationships"
      ]
    }
  ];

  const toggleRole = (id) => {
    if (activeRole === id) {
      setActiveRole(null);
    } else {
      setActiveRole(id);
    }
  };

  return (
    <>
      {/* Fixed Background Layer */}
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(29, 29, 29, 0.8)',
        zIndex: -3,
      }} />

      {/* Background Slideshow */}
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: -2,
        overflow: 'hidden',
        willChange: 'transform',
        opacity: bgOpacity,
        transition: 'opacity 1s ease'
      }}>
        <div style={{
          position: 'absolute',
          top: '-10%',
          left: '-10%',
          right: '-10%',
          bottom: '-10%',
          backgroundImage: `url(${backgrounds[bgIndex]})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          transform: `scale(${bgScale}) translate(${bgPosition.x}%, ${bgPosition.y}%)`,
          mixBlendMode: 'soft-light',
          transition: 'background-image 1s ease-in-out, transform 8s linear',
          willChange: 'transform, opacity',
          backfaceVisibility: 'hidden',
          transformStyle: 'preserve-3d',
        }} />
      </div>

      {/* Grid Container - Fades out after load */}
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: isMobile ? '100vw' : '50vw',
        bottom: 0,
        zIndex: -1,
        overflow: 'hidden',
        opacity: gridOpacity,
        transition: 'opacity 1s ease'
      }}>
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          width: isMobile ? '100vw' : '200vw',
          maskImage: isMobile ? 'none' : 'linear-gradient(to right, transparent 0%, white 5%, white 95%, transparent 100%)',
          WebkitMaskImage: isMobile ? 'none' : 'linear-gradient(to right, transparent 0%, white 5%, white 95%, transparent 100%)',
        }}>
          {gridStyles.map((style, index) => (
            <div key={index} style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundImage: `
                linear-gradient(to right, white 1px, transparent 1px),
                linear-gradient(to bottom, white 1px, transparent 1px)
              `,
              ...style
            }} />
          ))}
        </div>
      </div>

      {/* Desktop Header */}
      {!isMobile && (
        <>
          <div 
            style={{
              position: 'fixed',
              top: '30px',
              left: '30px',
              zIndex: 20,
              cursor: 'pointer',
              fontSize: '2rem',
              color: 'white',
            }}
            onClick={() => navigate('/')}
          >
            ×
          </div>

          <div style={{
            position: 'fixed',
            top: '40px',
            right: '40px',
            zIndex: 10,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            textAlign: 'left',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <img 
                src={LogoWhite} 
                alt="Outlne Logo"
                style={{
                  width: '40px',
                  height: 'auto',
                }}
              />
              <h1 style={{
                fontSize: '1.8rem',
                fontFamily: 'avenir-lt-pro, sans-serif',
                letterSpacing: '-1.2px',
                margin: 0,
                color: 'white',
                fontWeight: 500,
              }}>
                Outlne
              </h1>
            </div>
            <p style={{
              fontSize: '1rem',
              margin: '5px 0 0 0',
              color: 'white',
              fontFamily: 'avenir-lt-pro, sans-serif',
              fontWeight: '500'
            }}>
              Interior Design <span style={{
                backgroundColor: '#ffffff',
                color: 'black',
                padding: '2px 8px',
                borderRadius: '4px',
                display: 'inline-block',
                minWidth: '60px',
                textAlign: 'center'
              }}>
                Editor
              </span>
            </p>
          </div>
        </>
      )}

      {/* Mobile Header with Blur Background */}
      {isMobile && (
        <>
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            height: '110px',
            backgroundColor: 'rgba(31, 31, 31, 0.8)',
            backdropFilter: 'blur(10px)',
            zIndex: 15,
          }} />
          
          <div style={{
            position: 'fixed',
            top: '20px',
            left: '20px',
            zIndex: 20,
            textAlign: 'left',
          }}>
            <h1 style={{
              fontSize: '1.5rem',
              fontFamily: 'avenir-lt-pro, sans-serif',
              letterSpacing: '-1.2px',
              margin: 0,
              color: 'white',
              fontWeight: 500,
            }}>
              Outlne
            </h1>
            <p style={{
              fontSize: '0.9rem',
              margin: '5px 0 0 0',
              color: 'white',
              fontFamily: 'avenir-lt-pro, sans-serif',
              fontWeight: '500'
            }}>
              Interior Design <span style={{
                backgroundColor: '#ffffff',
                color: 'black',
                padding: '2px 6px',
                borderRadius: '4px',
                display: 'inline-block',
                minWidth: '50px',
                textAlign: 'center'
              }}>
                Editor
              </span>
            </p>
          </div>

          <div style={{
            position: 'fixed',
            top: '20px',
            right: '20px',
            zIndex: 20,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
            gap: '10px',
          }}>
            <img 
              src={LogoWhite} 
              alt="Outlne Logo"
              style={{
                width: '40px',
                height: '40px',
              }}
            />
            <div 
              style={{
                cursor: 'pointer',
                marginRight: '10px',
                fontSize: '1.8rem',
                color: 'white',
                marginTop: '-5px',
              }}
              onClick={() => navigate('/')}
            >
              ×
            </div>
          </div>
        </>
      )}

      {/* Main Content Container - Scrollable with Footer */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 5,
        overflowY: 'auto',
        overflowX: 'hidden',
        opacity: pageLoaded ? 1 : 0,
        transition: 'opacity 1s ease',
        overscrollBehavior: 'none'
      }}>
        
        {/* Content Wrapper */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          width: '100%',
          minHeight: '100vh',
          paddingTop: isMobile ? '130px' : '150px',
          paddingBottom: '0',
          position: 'relative',
          zIndex: 5,
        }}>
          
          {/* Scrollable Modal Container */}
          <div style={{
            width: isMobile ? '85vw' : isTablet ? '55vw' : '600px',
            height: isMobile ? 'auto' : isTablet ? 'auto' : 'auto',
            minHeight: isMobile ? 'auto' : '500px',
            border: '2px solid white',
            borderRadius: '40px',
            overflow: 'hidden',
            padding: isMobile ? '15px' : '20px',
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: 'rgba(34, 34, 34, 0.95)',
            marginBottom: '3rem',
          }}>
            
            {/* Careers Header */}
            <div style={{
              textAlign: 'center',
              marginBottom: isMobile ? '1.5rem' : '2rem',
              padding: isMobile ? '0 10px' : '0 20px',
              flexShrink: 0
            }}>
              <h1 style={{
                fontFamily: 'avenir-lt-pro, sans-serif',
                fontSize: isMobile ? '1.4rem' : isTablet ? '1.6rem' : '1.8rem',
                fontWeight: '500',
                color: 'white',
                marginBottom: '0.5rem',
                lineHeight: '1.2',
                letterSpacing: '-0.5px'
              }}>
                Careers
              </h1>
              <p style={{
                color: '#ccc',
                fontSize: isMobile ? '0.9rem' : isTablet ? '1rem' : '1.1rem',
                lineHeight: '1.4',
                fontFamily: 'avenir-lt-pro, sans-serif',
                margin: 0
              }}>
                We build things with balance,<br />& sustainability in mind. <br />Join us
              </p>
            </div>

            {/* Scrollable Roles Content */}
            <div style={{
              flex: 1,
              overflowY: 'auto',
              padding: isMobile ? '0 5px' : '0 10px',
              scrollbarWidth: 'thin',
              scrollbarColor: 'rgba(255,255,255,0.3) transparent',
              
              borderRadius: '12px',
              maxHeight: isMobile ? '400px' : '500px',
            }}>
              <style>
                {`
                  ::-webkit-scrollbar {
                    width: 6px;
                  }
                  ::-webkit-scrollbar-track {
                    background: transparent;
                  }
                  ::-webkit-scrollbar-thumb {
                    background: rgba(255,255,255,0.3);
                    border-radius: 3px;
                  }
                  ::-webkit-scrollbar-thumb:hover {
                    background: rgba(255,255,255,0.5);
                  }
                `}
              </style>
              
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: isMobile ? '1rem' : '1.5rem',
                padding: isMobile ? '15px 5px 20px 5px' : '20px 10px 20px 10px',
              }}>
                {roles.map((role) => (
                  <div key={role.id} 
                    onClick={() => toggleRole(role.id)}
                    style={{
                      backgroundColor: activeRole === role.id ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 255, 255, 0.05)',
                      border: activeRole === role.id ? '1px solid rgba(255, 255, 255, 0.3)' : '1px solid rgba(255, 255, 255, 0.1)',
                      borderRadius: '12px',
                      padding: isMobile ? '1rem' : '1.25rem',
                      transition: 'all 0.3s ease',
                      cursor: 'pointer',
                    }}
                    onMouseOver={(e) => {
                      if (activeRole !== role.id) {
                        e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.08)';
                        e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                      }
                    }}
                    onMouseOut={(e) => {
                      if (activeRole !== role.id) {
                        e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.05)';
                        e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                      }
                    }}
                  >
                    {/* Role Header */}
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: activeRole === role.id ? '1rem' : '0',
                    }}>
                      <h3 style={{
                        fontFamily: 'avenir-lt-pro, sans-serif',
                        fontSize: isMobile ? '0.95rem' : '1rem',
                        fontWeight: '500',
                        color: 'white',
                        margin: 0,
                        lineHeight: '1.3',
                      }}>
                        {role.title}
                      </h3>
                      <span style={{
                        color: '#ccc',
                        fontSize: '1.5rem',
                        transform: activeRole === role.id ? 'rotate(45deg)' : 'rotate(0)',
                        transition: 'transform 0.3s ease',
                        lineHeight: '1',
                      }}>
                        +
                      </span>
                    </div>
                    
                    {/* Short Description */}
                    <p style={{
                      color: '#ccc',
                      fontSize: isMobile ? '0.85rem' : '0.95rem',
                      lineHeight: '1.5',
                      fontFamily: 'avenir-lt-pro, sans-serif',
                      margin: '0.5rem 0 0 0',
                      display: activeRole === role.id ? 'none' : 'block',
                    }}>
                      {role.shortDesc}
                    </p>
                    
                    {/* Expanded Details */}
                    {activeRole === role.id && (
                      <div style={{
                        marginTop: '1rem',
                        color: '#ccc',
                        fontSize: isMobile ? '0.85rem' : '0.95rem',
                        lineHeight: '1.6',
                        fontFamily: 'avenir-lt-pro, sans-serif',
                      }}>
                        <div style={{ marginBottom: '1.5rem' }}>
                          <h4 style={{ 
                            color: 'white', 
                            margin: '0 0 0.5rem 0',
                            fontSize: isMobile ? '0.95rem' : '1rem',
                            fontFamily: 'avenir-lt-pro, sans-serif',
                            fontWeight: '500',
                          }}>
                            {role.fullTitle}
                          </h4>
                          <p style={{ margin: '0 0 0.5rem 0' }}>
                            <strong>Location:</strong> {role.location}
                          </p>
                          <p style={{ margin: '0 0 0.5rem 0' }}>
                            <strong>Compensation:</strong> {role.compensation}
                          </p>
                        </div>
                        
                        <div style={{ marginBottom: '1.5rem' }}>
                          <h4 style={{ 
                            color: 'white', 
                            margin: '0 0 0.5rem 0',
                            fontSize: isMobile ? '0.95rem' : '1rem',
                            fontFamily: 'avenir-lt-pro, sans-serif',
                            fontWeight: '500',
                          }}>
                            Your Role:
                          </h4>
                          <p style={{ margin: 0 }}>{role.mission}</p>
                        </div>
                        
                        <div>
                          <h4 style={{ 
                            color: 'white', 
                            margin: '0 0 0.5rem 0',
                            fontSize: isMobile ? '0.95rem' : '1rem',
                            fontFamily: 'avenir-lt-pro, sans-serif',
                            fontWeight: '500',
                          }}>
                            We're Looking For Someone Who:
                          </h4>
                          <ul style={{ 
                            margin: 0, 
                            paddingLeft: '1.2rem',
                            listStyleType: 'none'
                          }}>
                            {role.lookingFor.map((item, index) => (
                              <li key={index} style={{ 
                                marginBottom: '0.5rem',
                                position: 'relative',
                                paddingLeft: '1.2rem'
                              }}>
                                <span style={{
                                  position: 'absolute',
                                  left: 0,
                                  top: '0.4rem',
                                  width: '6px',
                                  height: '6px',
                                  backgroundColor: '#ccc',
                                  borderRadius: '50%'
                                }}></span>
                                {item}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Footer inside modal */}
            <div style={{
              textAlign: 'center',
              paddingTop: isMobile ? '1rem' : '1.5rem',
              marginTop: 'auto',
              flexShrink: 0,
              borderTop: '1px solid rgba(255, 255, 255, 0.2)'
            }}>
              <p style={{
                color: '#ccc',
                fontSize: isMobile ? '0.85rem' : '0.95rem',
                lineHeight: '1.5',
                fontFamily: 'avenir-lt-pro, sans-serif',
                margin: '0 0 1rem 0'
              }}>
                Interested? Send your resume to: careers@outlne.com
              </p>
              <button
                onClick={() => navigate('/')}
                style={{
                  padding: isMobile ? '10px 20px' : '12px 24px',
                  backgroundColor: 'white',
                  color: '#000',
                  border: 'none',
                  borderRadius: '25px',
                  fontFamily: 'avenir-lt-pro, sans-serif',
                  fontWeight: '500',
                  fontSize: isMobile ? '0.9rem' : '0.95rem',
                  cursor: 'pointer',
                  transition: 'background-color 0.3s ease',
                }}
                onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f0f0f0'}
                onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'white'}
              >
                Close
              </button>
            </div>
          </div>

          {/* Footer */}
          <div style={{
            width: '100%',
            position: 'relative',
            zIndex: 5,
            backgroundColor: '#1a1a1a',
          }}>
            <Footer />
          </div>
        </div>
      </div>

      {/* CSS Styles */}
      <style>
        {`
          html {
            overflow: hidden;
            height: 100%;
            width: 100%;
            position: fixed;
          }
          
          body {
            overflow: hidden;
            height: 100%;
            width: 100%;
            position: fixed;
            user-select: none;
            -webkit-user-select: none;
            -moz-user-select: none;
            margin: 0;
            padding: 0;
            overscroll-behavior: none;
            -webkit-overflow-scrolling: touch;
          }
          
          #root {
            height: 100%;
            width: 100%;
            overflow: hidden;
            position: fixed;
          }
          
          * {
            -webkit-font-smoothing: antialiased;
            -moz-osx-font-smoothing: grayscale;
            -webkit-tap-highlight-color: transparent;
            -webkit-touch-callout: none;
          }
          
          *:focus {
            outline: none;
          }
          
          div[style*="mask-image"] {
            pointer-events: none;
          }
        `}
      </style>
    </>
  );
}